import React, { useEffect, useState, useMemo } from "react";
import { api, apiErrorMessage, formatCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import ReceivePaymentDialog from "@/components/ReceivePaymentDialog";
import { toast } from "sonner";
import { Plus, X, CaretDown, MagnifyingGlass, Coins, Trash } from "@phosphor-icons/react";

const dateISO = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");

export default function EditOrderDialog({ open, onOpenChange, order, onSaved }) {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const [localOrder, setLocalOrder] = useState(null);
    const [payments, setPayments] = useState([]);

    const refreshOrderAndPayments = async (orderId) => {
        try {
            const [{ data: ord }, { data: pays }] = await Promise.all([
                api.get(`/sales/${orderId}`),
                api.get(`/payments`, { params: { sale_id: orderId } }),
            ]);
            setLocalOrder(ord);
            setPayments(pays);
            return ord;
        } catch (e) { return null; }
    };

    useEffect(() => {
        if (!open || !order) return;
        setCategoryFilter("all");
        setLocalOrder(order);
        setPayments([]);
        refreshOrderAndPayments(order.id);
        const c = order.customer_snapshot || {};
        setForm({
            customer_name: c.name || order.customer_name || "",
            mobile: c.mobile || "",
            whatsapp: c.whatsapp || "",
            email: c.email || "",
            gst_number: c.gst_number || "",
            address: c.address || order.delivery_address || "",
            dispatch_date: dateISO(order.dispatch?.expected_delivery),
            notes: order.notes || "",
            discount: order.discount || 0,
            delivery_charge: order.delivery_charge || 0,
            transportation_charge: order.transportation_charge || 0,
            items: (order.items || []).map((it) => ({
                product_id: it.product_id,
                hsn_code: it.hsn_code || "",
                quantity: it.quantity,
                unit_price: it.unit_price,
                gst_percent: it.gst_percent,
            })),
        });
        (async () => {
            try {
                const [{ data: prods }, { data: cats }] = await Promise.all([
                    api.get("/products", { params: { limit: 500, is_active: true } }),
                    api.get("/categories"),
                ]);
                setProducts(prods);
                setCategories(cats);
            } catch (e) { toast.error(apiErrorMessage(e)); }
        })();
    }, [open, order]);

    const filteredProducts = useMemo(() => {
        if (categoryFilter === "all") return products;
        return products.filter((p) => p.category_id === categoryFilter);
    }, [products, categoryFilter]);

    const categoryMap = useMemo(() => {
        const m = {};
        for (const c of categories) m[c.id] = c.name;
        return m;
    }, [categories]);

    const totals = useMemo(() => {
        if (!form) return { subtotal: 0, tax: 0, dc: 0, tc: 0, grand: 0 };
        let subtotal = 0, tax = 0;
        for (const it of form.items) {
            const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
            subtotal += line;
            tax += line * Number(it.gst_percent || 0) / 100;
        }
        const discount = Number(form.discount || 0);
        const dc = Number(form.delivery_charge || 0);
        const tc = Number(form.transportation_charge || 0);
        return { subtotal, tax, dc, tc, grand: subtotal + tax + dc + tc - discount };
    }, [form]);

    if (!form) return null;

    const updateItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", hsn_code: "", quantity: "", unit_price: "", gst_percent: 18 }] }));
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const onProductSelect = (idx, productId) => {
        const p = products.find((x) => x.id === productId);
        updateItem(idx, {
            product_id: productId,
            hsn_code: p?.hsn_code || "",
            unit_price: p ? Number(p.selling_price || 0) : 0,
            gst_percent: p ? Number(p.gst_percent || 18) : 18,
        });
    };

    const submit = async () => {
        if (!form.customer_name.trim()) return toast.error("Customer name zaruri hai");
        const validItems = form.items.filter((it) => it.product_id && Number(it.quantity) > 0);
        if (validItems.length === 0) return toast.error("Kam se kam ek product chahiye");

        setSaving(true);
        try {
            const payload = {
                customer_name: form.customer_name.trim(),
                mobile: (form.mobile || "").replace(/[^0-9+]/g, ""),
                whatsapp: form.whatsapp || "",
                email: form.email || "",
                gst_number: form.gst_number || "",
                address: form.address || "",
                dispatch_date: form.dispatch_date ? new Date(form.dispatch_date).toISOString() : null,
                notes: form.notes || "",
                discount: Number(form.discount || 0),
                delivery_charge: Number(form.delivery_charge || 0),
                transportation_charge: Number(form.transportation_charge || 0),
                items: validItems.map((it) => ({
                    product_id: it.product_id,
                    quantity: Number(it.quantity),
                    unit_price: Number(it.unit_price),
                    gst_percent: Number(it.gst_percent || 0),
                    hsn_code: it.hsn_code || "",
                })),
            };
            const { data } = await api.patch(`/sales/${order.id}`, payload);
            toast.success("Order updated!");
            onOpenChange(false);
            onSaved && onSaved(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="edit-order-dialog">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold font-display tracking-tight">
                        Edit Order · <span className="font-mono">{order.order_no}</span>
                    </DialogTitle>
                </DialogHeader>

                {/* Customer section */}
                <div className="eyebrow -mt-2">Customer</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Customer Naam</Label>
                        <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                               className="mt-1.5 h-11" data-testid="edit-name" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Mobile Number</Label>
                        <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                               placeholder="+91 98xxxxxxxx" className="mt-1.5 h-11" data-testid="edit-mobile" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">WhatsApp</Label>
                        <Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                               placeholder="Same as mobile" className="mt-1.5 h-11" data-testid="edit-whatsapp" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">GST Number</Label>
                        <Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
                               className="mt-1.5 h-11 font-mono" data-testid="edit-gst" />
                    </div>
                </div>
                <div>
                    <Label className="text-sm font-semibold text-neutral-800">Poora Address</Label>
                    <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                              rows={2} className="mt-1.5" data-testid="edit-address" />
                </div>
                <div>
                    <Label className="text-sm font-semibold text-neutral-800">Dispatch Date (planned)</Label>
                    <Input type="date" value={form.dispatch_date}
                           onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })}
                           className="mt-1.5 h-11" data-testid="edit-dispatch-date" />
                </div>

                {/* Products */}
                <div className="flex items-center justify-between mt-2">
                    <div className="eyebrow">Products</div>
                    <Button variant="outline" size="sm" onClick={addItem} className="rounded-full" data-testid="edit-add-item">
                        <Plus size={14} className="mr-1" /> Item Jodein
                    </Button>
                </div>
                {categories.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap -mt-1" data-testid="edit-category-filter">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Category:</span>
                        <button type="button" onClick={() => setCategoryFilter("all")}
                                className={`text-xs px-3 py-1 rounded-full border transition ${categoryFilter === "all" ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"}`}>
                            All ({products.length})
                        </button>
                        {categories.map((c) => {
                            const count = products.filter((p) => p.category_id === c.id).length;
                            if (count === 0) return null;
                            return (
                                <button key={c.id} type="button" onClick={() => setCategoryFilter(c.id)}
                                        className={`text-xs px-3 py-1 rounded-full border transition ${categoryFilter === c.id ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400"}`}>
                                    {c.name} ({count})
                                </button>
                            );
                        })}
                    </div>
                )}
                <div className="space-y-2">
                    {form.items.map((it, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center rounded-md p-3 border border-neutral-100" style={{ background: "#F5F7F2" }} data-testid={`edit-item-${idx}`}>
                            <div className="col-span-12 md:col-span-3">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Product</div>
                                <ProductPicker
                                    products={filteredProducts}
                                    categoryMap={categoryMap}
                                    value={it.product_id}
                                    onSelect={(pid) => onProductSelect(idx, pid)}
                                    testId={`edit-product-picker-${idx}`}
                                />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">HSN</div>
                                <Input value={it.hsn_code || ""} onChange={(e) => updateItem(idx, { hsn_code: e.target.value })} className="h-10 bg-white font-mono text-xs" placeholder="7019" />
                            </div>
                            <div className="col-span-3 md:col-span-1">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Qty</div>
                                <Input type="number" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="h-10 bg-white text-center px-1" />
                            </div>
                            <div className="col-span-3 md:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Rate ₹</div>
                                <Input type="number" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="h-10 bg-white" />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">GST %</div>
                                <Input type="number" value={it.gst_percent} onChange={(e) => updateItem(idx, { gst_percent: e.target.value })} className="h-10 bg-white text-center px-1" />
                            </div>
                            <div className="col-span-6 md:col-span-1 text-right">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-1">Total</div>
                                <div className="h-10 flex items-center justify-end tabular font-bold" style={{ color: "#15803D" }}>
                                    {formatCurrency(Number(it.quantity || 0) * Number(it.unit_price || 0) * (1 + Number(it.gst_percent || 0) / 100))}
                                </div>
                            </div>
                            <button onClick={() => removeItem(idx)} disabled={form.items.length === 1}
                                    className="col-span-2 md:col-span-1 h-10 flex items-center justify-center text-neutral-500 hover:text-red-600 disabled:opacity-30" data-testid={`edit-remove-${idx}`}>
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Delivery + Transportation charges */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Delivery Charge ₹</Label>
                        <Input type="number" value={form.delivery_charge}
                               onChange={(e) => setForm({ ...form, delivery_charge: e.target.value, final_amount: undefined })}
                               placeholder="0" className="mt-1.5 h-11" data-testid="edit-delivery-charge" />
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Transportation Charge ₹</Label>
                        <Input type="number" value={form.transportation_charge}
                               onChange={(e) => setForm({ ...form, transportation_charge: e.target.value, final_amount: undefined })}
                               placeholder="0" className="mt-1.5 h-11" data-testid="edit-transportation-charge" />
                    </div>
                </div>

                {/* Amount adjustments + Notes + Total */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                    <div className="space-y-2">
                        <div>
                            <Label className="text-sm font-semibold text-neutral-800">Final Amount ₹</Label>
                            <Input
                                type="number"
                                value={form.final_amount ?? Math.max(0, totals.subtotal + totals.tax + totals.dc + totals.tc - Number(form.discount || 0))}
                                onChange={(e) => {
                                    const finalAmt = e.target.value === "" ? "" : Number(e.target.value);
                                    const preTotal = totals.subtotal + totals.tax + totals.dc + totals.tc;
                                    const newDiscount = finalAmt === "" ? 0 : Math.max(0, preTotal - finalAmt);
                                    setForm({ ...form, final_amount: finalAmt, discount: newDiscount });
                                }}
                                className="mt-1.5 h-11 font-bold"
                                placeholder="Grand total override"
                                data-testid="edit-final-amount"
                            />
                            <p className="text-[10px] text-neutral-500 mt-1">Amount jo customer ko dena hai</p>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold text-neutral-600">Discount ₹ (auto)</Label>
                            <Input
                                type="number"
                                value={form.discount}
                                onChange={(e) => {
                                    const disc = Number(e.target.value || 0);
                                    setForm({ ...form, discount: e.target.value, final_amount: Math.max(0, totals.subtotal + totals.tax + totals.dc + totals.tc - disc) });
                                }}
                                className="mt-1.5 h-9 text-sm"
                                data-testid="edit-discount"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-sm font-semibold text-neutral-800">Notes</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                  rows={5} className="mt-1.5" data-testid="edit-notes" />
                    </div>
                    <div className="rounded-md p-4 space-y-3" style={{ background: "#F5F7F2" }} data-testid="edit-total-summary">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Grand Total</div>
                            <div className="text-2xl font-extrabold font-display tabular mt-1" style={{ color: "#15803D" }}>
                                {formatCurrency(totals.grand)}
                            </div>
                            <div className="text-[10px] text-neutral-500 mt-1 space-y-0.5">
                                <div>Subtotal {formatCurrency(totals.subtotal)}</div>
                                <div>GST {formatCurrency(totals.tax)}</div>
                                {totals.dc > 0 && <div>Delivery +{formatCurrency(totals.dc)}</div>}
                                {totals.tc > 0 && <div>Transport +{formatCurrency(totals.tc)}</div>}
                                {Number(form.discount || 0) > 0 && (
                                    <div style={{ color: "#DC2626" }}>Discount −{formatCurrency(Number(form.discount || 0))}</div>
                                )}
                            </div>
                        </div>
                        {localOrder && (
                            <div className="border-t border-neutral-200 pt-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-neutral-500 font-semibold uppercase tracking-wider">Paid so far</span>
                                    <span className="tabular font-bold" style={{ color: "#15803D" }}>{formatCurrency(localOrder.paid_amount || 0)}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs mt-1">
                                    <span className="text-neutral-500 font-semibold uppercase tracking-wider">Baaki</span>
                                    <span className="tabular font-bold" style={{ color: (localOrder.balance_amount ?? Math.max(0, totals.grand - (localOrder.paid_amount || 0))) > 0 ? "#DC2626" : "#15803D" }}>
                                        {formatCurrency(localOrder.balance_amount ?? Math.max(0, totals.grand - (localOrder.paid_amount || 0)))}
                                    </span>
                                </div>

                                {payments.length > 0 && (
                                    <div className="mt-2 space-y-1 max-h-32 overflow-y-auto" data-testid="edit-payment-history">
                                        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Payment History</div>
                                        {payments.map((p) => (
                                            <div key={p.id} className="flex items-center justify-between gap-2 bg-white rounded px-2 py-1 border border-neutral-200" data-testid={`edit-payment-row-${p.id}`}>
                                                <div className="min-w-0 flex-1 text-[11px]">
                                                    <div className="font-mono truncate">{p.receipt_no}</div>
                                                    <div className="text-neutral-500 uppercase tracking-wider">
                                                        {(p.payment_mode || "cash").toUpperCase()}
                                                    </div>
                                                </div>
                                                <span className="text-xs font-bold tabular shrink-0" style={{ color: "#15803D" }}>
                                                    {formatCurrency(p.amount)}
                                                </span>
                                                <button
                                                    onClick={async () => {
                                                        if (!window.confirm(`Payment ${p.receipt_no} (${formatCurrency(p.amount)}) delete karein?`)) return;
                                                        try {
                                                            await api.delete(`/payments/${p.id}`);
                                                            toast.success("Payment deleted");
                                                            await refreshOrderAndPayments(localOrder.id);
                                                            onSaved && onSaved();
                                                        } catch (e) { toast.error(apiErrorMessage(e)); }
                                                    }}
                                                    className="text-red-500 hover:text-red-700 shrink-0"
                                                    title="Delete payment"
                                                    data-testid={`edit-payment-delete-${p.id}`}
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <Button
                                    onClick={() => setPayOpen(true)}
                                    variant="outline"
                                    className="w-full mt-2 border-2"
                                    style={{ borderColor: "#15803D", color: "#15803D" }}
                                    data-testid="edit-open-payment-btn"
                                >
                                    <Coins size={14} className="mr-1.5" /> Payment Add karein
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="edit-submit">
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
            <ReceivePaymentDialog
                open={payOpen}
                onOpenChange={setPayOpen}
                defaultSaleId={localOrder?.id}
                defaultCustomerId={localOrder?.customer_id || ""}
                defaultInvoiceNo={localOrder?.order_no}
                onSaved={async () => {
                    setPayOpen(false);
                    const refreshed = await refreshOrderAndPayments(localOrder.id);
                    if (refreshed) {
                        toast.success("Payment recorded");
                        onSaved && onSaved(refreshed);
                    }
                }}
            />
        </Dialog>
    );
}

function ProductPicker({ products, categoryMap, value, onSelect, testId }) {
    const [open, setOpen] = useState(false);
    const selected = products.find((p) => p.id === value);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    role="combobox"
                    aria-expanded={open}
                    className="h-10 w-full flex items-center justify-between rounded-md border border-neutral-200 bg-white px-3 text-sm hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    data-testid={testId}
                >
                    <span className={selected ? "text-neutral-900 truncate" : "text-neutral-400"}>
                        {selected ? `${selected.name} · Stock ${selected.current_stock}` : "Product chunein"}
                    </span>
                    <CaretDown size={14} className="ml-2 shrink-0 opacity-60" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[min(92vw,420px)]" align="start">
                <Command
                    filter={(itemValue, search) => {
                        const q = search.toLowerCase().trim();
                        if (!q) return 1;
                        return itemValue.toLowerCase().includes(q) ? 1 : 0;
                    }}
                >
                    <div className="flex items-center border-b border-neutral-200 px-3">
                        <MagnifyingGlass size={14} className="opacity-50 shrink-0" />
                        <CommandInput placeholder="Search by name / SKU / barcode..." className="border-0 focus:ring-0 h-11" />
                    </div>
                    <CommandList className="max-h-72">
                        <CommandEmpty className="py-6 text-center text-xs text-neutral-500">
                            Koi product nahi mila
                        </CommandEmpty>
                        <CommandGroup>
                            {products.map((p) => {
                                const catName = categoryMap[p.category_id] || "";
                                const searchable = `${p.name} ${p.sku || ""} ${p.barcode || ""} ${catName}`;
                                return (
                                    <CommandItem
                                        key={p.id}
                                        value={searchable}
                                        onSelect={() => { onSelect(p.id); setOpen(false); }}
                                        className="cursor-pointer flex items-center justify-between gap-2 py-2"
                                    >
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-medium truncate">{p.name}</span>
                                            <span className="text-[11px] text-neutral-500 font-mono">
                                                {p.sku || "—"}{catName ? ` · ${catName}` : ""}
                                            </span>
                                        </div>
                                        <span className="text-[11px] font-semibold shrink-0 tabular" style={{ color: p.current_stock <= 0 ? "#DC2626" : "#15803D" }}>
                                            Stock {p.current_stock}
                                        </span>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
